import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TablePagination, Tooltip } from '@mui/material';

import styles from './PaginationFooter.module.scss';
import { FE_VERSION } from '../../../consts/version';
import { useDimensions } from '../../../helpers';

type TUserPagination = {
  total: number;
  page: number;
  onPageChange: (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  hideVersion?: boolean;
  pinnedCount?: number;
};

export const PaginationFooter: React.FC<TUserPagination> = ({
  total,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  hideVersion,
  pinnedCount = 0,
}) => {
  const { widthD } = useDimensions();
  const showPinnedCount = page === 0 && pinnedCount > 0;
  const footerRef = useRef<HTMLElement>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const footer = footerRef.current;
    const container = anchorRef.current?.parentElement;
    if (!footer || !container) return;

    const lists = [container, ...Array.from(container.querySelectorAll<HTMLElement>('*'))].filter(
      (element) =>
        !footer.contains(element) && element.getClientRects().length > 0 && /^(auto|scroll)$/.test(getComputedStyle(element).overflowY),
    );
    const previous = lists.map((element) => element.style.maxHeight);
    const fitLists = () => {
      const bottom = footer.getBoundingClientRect().top;
      lists.forEach((element) => {
        const style = getComputedStyle(element);
        const border = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
        const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
        const inset = style.boxSizing === 'border-box' ? 0 : border + padding;
        const height = Math.max(0, bottom - element.getBoundingClientRect().top - inset);
        const maxHeight = `${height}px`;
        if (element.style.maxHeight !== maxHeight) element.style.maxHeight = maxHeight;
      });
    };

    fitLists();
    let frame: number | null = null;
    const scheduleFit = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        fitLists();
      });
    };
    const observer = new ResizeObserver(scheduleFit);
    observer.observe(container);
    observer.observe(footer);
    Array.from(container.children).forEach((element) => observer.observe(element));
    window.addEventListener('resize', scheduleFit);
    container.addEventListener('transitionend', scheduleFit);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', scheduleFit);
      container.removeEventListener('transitionend', scheduleFit);
      if (frame !== null) cancelAnimationFrame(frame);
      lists.forEach((element, index) => {
        element.style.maxHeight = previous[index];
      });
    };
  }, [widthD, total, page, rowsPerPage]);

  return (
    <>
      <span ref={anchorRef} hidden />
      {createPortal(
        <footer ref={footerRef} className={styles['footer-pagination']}>
          {!hideVersion && widthD >= 768 && <div className={styles.version}>Amplify version: {FE_VERSION}</div>}
          <TablePagination
            className={styles['pagination-component']}
            classes={{ actions: styles['pagination-class-custom'] }}
            component="div"
            id={'footer-custom-rows-per-page-selector'}
            count={total}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            labelDisplayedRows={({ from, to, count }) => (
              <span>
                {from}-{to}
                {showPinnedCount && (
                  <Tooltip title={`includes +${pinnedCount} pinned`} arrow>
                    <span className={styles.pinnedCount}>+{pinnedCount}</span>
                  </Tooltip>
                )}
                {' '}
                of {count}
              </span>
            )}
            sx={{
              '& .MuiTablePagination-selectLabel': {
                display: widthD < 768 ? 'none' : undefined,
              },
              '& .MuiTablePagination-input': {
                borderRadius: '6px !important',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: '#0a1929',
                padding: '5px 6px !important',
                '& svg': {
                  color: 'white',
                  paddingRight: '6px !important',
                  fontSize: '20px !important',
                },
              },
            }}
          />
        </footer>,
        document.body,
      )}
    </>
  );
};
